import { useEffect, useState } from 'react';

import { announce } from '@/lib/announcer';

export interface UseOnlineStatusOptions {
  /** Already localized. Announced once when the player goes offline. */
  offlineAnnouncement?: string | undefined;
  /** Already localized. Announced once when the connection returns. */
  onlineAnnouncement?: string | undefined;
}

/**
 * `navigator.onLine` plus the `online`/`offline` events. The events are the source of truth after
 * mount: `navigator.onLine` is a snapshot and is wrong in some webviews, but it is the right
 * initial value so the banner does not flash on a connected first paint.
 *
 * Announces each transition once. Polling connectivity would chatter the live region.
 */
export function useOnlineStatus({
  offlineAnnouncement,
  onlineAnnouncement,
}: UseOnlineStatusOptions = {}): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    const onOnline = () => {
      setOnline(true);
      if (onlineAnnouncement !== undefined && onlineAnnouncement !== '') {
        announce(onlineAnnouncement, 'polite');
      }
    };
    const onOffline = () => {
      setOnline(false);
      if (offlineAnnouncement !== undefined && offlineAnnouncement !== '') {
        announce(offlineAnnouncement, 'polite');
      }
    };

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [offlineAnnouncement, onlineAnnouncement]);

  return online;
}
